/* ==========================================================================
   Rook UI Core — Toggle & Toggle Group Controllers
   Web Components: <rk-toggle-element> & <rk-toggle-group>
   ========================================================================== */

if (!customElements.get('rk-toggle-element')) {
    class RkToggleElement extends HTMLElement {
        constructor() {
            super();
            this.button = null;
            this._onClick = this._onClick.bind(this);
            this._onKeyDown = this._onKeyDown.bind(this);
        }

        connectedCallback() {
            this.button = this.querySelector('button');
            if (!this.button) return;

            // Ensure pressed state is properly set initially
            if (!this.button.hasAttribute('aria-pressed')) {
                this.button.setAttribute('aria-pressed', 'false');
            }

            this.button.addEventListener('click', this._onClick);
            this.button.addEventListener('keydown', this._onKeyDown);
        }

        disconnectedCallback() {
            if (this.button) {
                this.button.removeEventListener('click', this._onClick);
                this.button.removeEventListener('keydown', this._onKeyDown);
            }
        }

        get isPressed() {
            return this.button?.getAttribute('aria-pressed') === 'true';
        }

        setPressed(value) {
            if (!this.button || this.button.disabled) return;

            const state = value ? 'true' : 'false';
            this.button.setAttribute('aria-pressed', state);

            if (value) {
                this.button.classList.add('rk-toggle--on');
                this.button.classList.remove('rk-toggle--off');
            } else {
                this.button.classList.add('rk-toggle--off');
                this.button.classList.remove('rk-toggle--on');
            }

            this.dispatchEvent(new CustomEvent('rk:toggle:change', {
                bubbles: true,
                detail: { pressed: value, value: this.dataset.value }
            }));
        }

        toggle() {
            this.setPressed(!this.isPressed);
        }

        _onClick(e) {
            // If it's part of a group, let the group manage the selection logic
            const group = this.closest('rk-toggle-group');
            if (!group) {
                this.toggle();
            }
        }

        _onKeyDown(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.button.click();
            }
        }
    }

    customElements.define('rk-toggle-element', RkToggleElement);
}

if (!customElements.get('rk-toggle-group')) {
    class RkToggleGroup extends HTMLElement {
        constructor() {
            super();
            this.type = this.dataset.type || 'single'; // 'single' or 'multiple'
        }

        connectedCallback() {
            this.addEventListener('rk:toggle:change', this._onToggleChange.bind(this));

            // Set initial role
            if (!this.hasAttribute('role')) {
                this.setAttribute('role', 'group');
            }
        }

        get toggles() {
            return Array.from(this.querySelectorAll('rk-toggle-element'));
        }

        _onToggleChange(e) {
            e.stopPropagation(); // Stop the pure toggle event

            const clickedToggle = e.target;
            const isNowPressed = clickedToggle.isPressed;

            // Revert the pure toggle action first, because group manages it
            clickedToggle.setPressed(!isNowPressed);

            if (this.type === 'single') {
                this._handleSingle(clickedToggle);
            } else {
                this._handleMultiple(clickedToggle);
            }
        }

        _handleSingle(toggle) {
            const wasPressed = toggle.isPressed;

            // Unpress all
            this.toggles.forEach(t => t.setPressed(false));

            // If it wasn't pressed before, press it now. If it was, let it stay unpressed (can unselect).
            if (!wasPressed) {
                toggle.setPressed(true);
            }

            this._emitChange();
        }

        _handleMultiple(toggle) {
            // Just toggle the current one natively
            toggle.setPressed(!toggle.isPressed);
            this._emitChange();
        }

        _emitChange() {
            const selected = this.toggles
                .filter(t => t.isPressed)
                .map(t => t.dataset.value);

            let value = this.type === 'single' ? (selected[0] || null) : selected;

            this.dispatchEvent(new CustomEvent('rk:toggle-group:change', {
                bubbles: true,
                detail: { value }
            }));
        }
    }

    customElements.define('rk-toggle-group', RkToggleGroup);
}
